import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField,
  IconButton,
  Tooltip
} from '@mui/material';
import { 
  YouTube as YouTubeIcon,
  FolderOpen as FolderOpenIcon,
  PlayArrow as PlayArrowIcon,
  CloudUpload as CloudUploadIcon,
  History as HistoryIcon,
  Bookmark as BookmarkIcon,
  Login as LoginIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AuthDialog from '../components/AuthDialog';
import { youtubeApi } from '../services/youtubeApi';
import { saveVbidsToFirestore, extractYouTubeId } from '../utils/vbidIO';
import { collection, query, where, getDocs, orderBy, limit, doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { forceRefresh, checkAndClearInfiniteLoops } from '../utils/forceRefresh';
// import { setupMessageDebugger } from '../utils/messageDebugger'; // 🔧 暫時禁用消息調試器
// import '../utils/emergencyFix'; // 🔧 暫時禁用緊急修復工具，避免 DOM 衝突

interface OpenPageProps {
  onPageChange?: (page: string, pageState?: { currentSection?: string; expandedMain?: string[] }) => void;
  isEmbedded?: boolean;
}

const OpenPage: React.FC<OpenPageProps> = ({ 
  onPageChange,
  isEmbedded = false 
}) => {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const [youtubeDialogOpen, setYoutubeDialogOpen] = useState(false);
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [fileInputRef] = useState(React.createRef<HTMLInputElement>());
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const [urlError, setUrlError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [videoSearchResult, setVideoSearchResult] = useState<any>(null);
  const [searchResultDialogOpen, setSearchResultDialogOpen] = useState(false);
  
  // 🔧 新增：檔案選擇對話框相關狀態
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileSelectionDialogOpen, setFileSelectionDialogOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // 🔧 優化：只在組件掛載時記錄一次
  useEffect(() => {
    // 调试日志已移除
    // 调试日志已移除
    // 调试日志已移除
    
    // 🔧 暫時禁用消息調試器，避免消息循環
    // const messageDebugger = setupMessageDebugger();
    // 调试日志已移除
    
    return () => {
      // console.log('🔧 消息調試器統計:', {
      //   messageCount: messageDebugger.getMessageCount(),
      //   messageLog: messageDebugger.getMessageLog()
      // });
    };
  }, []); // 只在組件掛載時記錄一次

  // 監聽登入狀態變化，執行待執行的行動
  useEffect(() => {
    if (authUser?.email && pendingAction) {
      // 调试日志已移除
      
      if (pendingAction === 'notes') {
        // 前往筆記頁面（與 BottomBar 的筆記功能保持一致）
        navigate('/notes');
      }
      
      // 清除待執行的行動
      setPendingAction(null);
    }
  }, [authUser, pendingAction, navigate]);

  // 查詢現有影片
  const searchExistingVideo = async (videoId: string) => {
    try {
      // 调试日志已移除
      
      // 查詢 sharedVbids 集合 - 移除 orderBy 避免索引問題
      const sharedVbidsQuery = query(
        collection(db, "sharedVbids"),
        where("videoId", "==", videoId)
      );
      
      const sharedVbidsSnapshot = await getDocs(sharedVbidsQuery);
      // 调试日志已移除
      
      // 🚀 優化：跳過 userNotes 查詢，因為 sharedVbids 是主要資料來源
      // const userNotesQuery = query(
      //   collection(db, "userNotes"),
      //   where("videoId", "==", videoId)
      // );
      
      // const userNotesSnapshot = await getDocs(userNotesQuery);
      // 调试日志已移除
      
      // 分析結果
      const myVideos: any[] = [];
      const otherVideos: any[] = [];
      const totalNotes = sharedVbidsSnapshot.size; // 🚀 優化：使用 sharedVbids 數量
      const uniqueUsers = new Set<string>();
      
      // 處理 sharedVbids
      sharedVbidsSnapshot.forEach(doc => {
        const data = doc.data();
        // 调试日志已移除
        
        // 統一使用 UID 比較，因為資料庫中的 userId 都是 UID
        if (data.userId === authUser?.uid) {
          myVideos.push({ ...data, docId: doc.id, source: 'sharedVbids' });
          console.log('✅ 找到本人的 sharedVbids 記錄');
        } else {
          otherVideos.push({ ...data, docId: doc.id, source: 'sharedVbids' });
          // 调试日志已移除
        }
        if (data.userId) uniqueUsers.add(data.userId);
      });
      
      // 🚀 優化：跳過 userNotes 處理，因為已遷移到 sharedVbids
      // userNotes 處理已移除，所有資料現在都在 sharedVbids 中
      
      const result = {
        videoId,
        myVideos,
        otherVideos,
        totalNotes,
        uniqueUserCount: uniqueUsers.size,
        hasMyVideo: myVideos.length > 0,
        hasOtherVideo: otherVideos.length > 0
      };
      
      // 调试日志已移除
      // 调试日志已移除
      // 调试日志已移除
      // 调试日志已移除
      // 调试日志已移除
      
      return result;
      
    } catch (error) {
      console.error('❌ 查詢現有影片失敗:', error);
      // 查詢失敗時返回空結果，而不是 null
      return {
        videoId,
        myVideos: [],
        otherVideos: [],
        totalNotes: 0,
        uniqueUserCount: 0,
        hasMyVideo: false,
        hasOtherVideo: false
      };
    }
  };

  // 處理 YouTube 按鈕點擊
  const handleYouTubeClick = () => {
    setYoutubeDialogOpen(true);
  };

  // 保存影片記錄到 Firestore
  // 🔧 新增：保存鎖，防止重複保存（使用 Set 記錄正在保存的影片）
  const savingVideosRef = useRef<Set<string>>(new Set());
  
  const saveVideoToFirestore = async (videoUrl: string, youtubeInfo: any) => {
    if (!authUser?.uid) {
      throw new Error('用戶未登入');
    }

    const videoId = extractYouTubeId(videoUrl);
    if (!videoId) {
      throw new Error('無法提取 YouTube 影片 ID');
    }

    const userId = authUser.uid;
    const lockKey = `${userId}_${videoId}`;
    
    // 🔧 修復：檢查是否正在保存，避免並發保存
    if (savingVideosRef.current.has(lockKey)) {
      console.log('⚠️ 影片正在保存中，跳過重複請求:', lockKey);
      return;
    }
    
    // 加鎖
    savingVideosRef.current.add(lockKey);
    console.log('🔒 開始保存影片，已加鎖:', lockKey);
    
    try {
      // 🔧 修復：檢查是否已存在，避免重複保存
      const { getDb } = await import('../services/firebase');
      const firestoreDb = await getDb();
      const { collection, query, where, getDocs } = await import('firebase/firestore');
      
      const existingQuery = query(
        collection(firestoreDb, 'sharedVbids'),
        where('userId', '==', userId),
        where('videoId', '==', videoId)
      );
      const existingSnapshot = await getDocs(existingQuery);
      
      if (!existingSnapshot.empty) {
        const existingDocId = existingSnapshot.docs[0].id;
        console.log('⚠️ 影片已存在，跳過保存:', { 
          videoId, 
          文檔數量: existingSnapshot.size,
          已存在的文檔ID: existingDocId
        });
        // 🔧 修復：返回已存在的文檔 ID，讓 handleYouTubeConfirm 可以使用
        return existingDocId;
      }
      
      console.log('✅ 影片不存在，開始保存:', { 
        videoId, 
        userId,
        將生成的compositeId: `${userId}_${videoId}_v1` 
      });
      
      // 準備影片數據
      const videoData: any = {
        videoId: videoId,
        path: videoUrl,
        title: youtubeInfo?.title || '未知標題',
        provider: 'YouTube',
        readUsers: [userId],  // 🔧 添加建立者到觀看記錄
        lastReadAt: new Date().toISOString(),
        flag: 'active',
        privacy: 'Public',
        zones: ['personal'],
        groupIds: [] // 🔧 修復：初始化 groupIds 字段，讓影片可以被分配到群組
      };

      // 只有在有 YouTube 資訊時才添加相關欄位
      if (youtubeInfo) {
        videoData.youtubeInfo = {
          title: youtubeInfo.title,
          channelTitle: youtubeInfo.channelTitle,
          viewCount: youtubeInfo.viewCount,
          publishedAt: youtubeInfo.publishedAt,
          thumbnails: youtubeInfo.thumbnails,
          description: youtubeInfo.description
        };
        videoData.youtubeMetadata = {
          hashtags: youtubeInfo.tags || [],
          title: youtubeInfo.title || '',
          description: youtubeInfo.description || '',
          channelTitle: youtubeInfo.channelTitle || '',
          publishedAt: youtubeInfo.publishedAt || '',
          viewCount: youtubeInfo.viewCount || '0',
          thumbnail: youtubeInfo.thumbnails?.high?.url || youtubeInfo.thumbnails?.medium?.url || '',
          duration: youtubeInfo.duration || '0:00'
        };
      }

      // 使用統一的影片數據保存服務
      const { saveVideoDataUnified } = await import('../services/videoDataService');
      const compositeId = await saveVideoDataUnified(videoData, userId, 'v1');
      console.log('✅ 影片記錄已保存到 sharedVbids:', compositeId);
      
      // 🔧 修復：返回 compositeId 供 handleYouTubeConfirm 使用
      return compositeId;
    } catch (error) {
      console.error('❌ 保存影片失敗:', error);
      throw error;
    } finally {
      // 🔧 修復：無論成功或失敗，都要釋放鎖
      savingVideosRef.current.delete(lockKey);
      console.log('🔓 已釋放保存鎖:', lockKey);
    }
  };

  // 導航到 MainPage 的輔助函數
  const navigateToMainPage = (videoUrl: string, youtubeInfo: any, compositeId?: string) => {
    const videoId = extractYouTubeId(videoUrl);
    
    // 🔧 修復：設置用戶主動播放標記，確保觀看記錄被正確記錄
    (window as any).__checkplay_user_initiated_play = true;
    console.log('✅ OpenPage: 已標記為用戶主動播放，將記錄到瀏覽記錄');
    
    if (videoId) {
      // 使用 /v/ 路由格式，與 ManagePages 保持一致
      navigate(`/v/${videoId}`, {
        state: {
          videoUrl,
          source: 'youtube',
          youtubeInfo,
          fromOpenPage: true,
          documentId: compositeId || videoId, // 🔧 修復：優先使用完整的 compositeId
          firestoreId: compositeId || videoId // 🔧 新增：直接傳遞 firestoreId
        }
      });
    } else {
      // 如果無法提取 videoId，使用原來的路由
      navigate('/main', { 
        state: { 
          videoUrl,
          source: 'youtube',
          youtubeInfo,
          firestoreId: compositeId // 🔧 新增：也為舊路由傳遞 firestoreId
        } 
      });
    }
  };

  // 處理 YouTube URL 確認 - 超快速版本
  const handleYouTubeConfirm = async () => {
    if (!youtubeUrl.trim()) {
      setUrlError('請輸入 YouTube 影片網址');
      return;
    }

    setIsProcessing(true);
    setUrlError(null);

    try {
      const trimmedUrl = youtubeUrl.trim();
      console.log('🎬 處理 YouTube URL:', trimmedUrl);

      // 驗證 YouTube URL 格式
      const videoId = youtubeApi.extractVideoId(trimmedUrl);
      if (!videoId) {
        setUrlError('目前只支援 YouTube 影片');
        setIsProcessing(false);
        return;
      }

      console.log('✅ YouTube URL 驗證通過，Video ID:', videoId);

      // 🔧 修復：先保存影片並獲取完整的 compositeId，再導航
      // 调试日志已移除
      
      // 🔧 修復：避免重複調用 YouTube API，只調用一次並共享結果
      const youtubeInfoPromise = youtubeApi.getVideoDetails(videoId).then(youtubeInfo => {
        // 调试日志已移除
        return youtubeInfo;
      }).catch(error => {
        console.warn('⚠️ 獲取 YouTube 詳細資訊失敗:', error);
        return null;
      });

      // 等待 YouTube 資訊後保存影片
      const youtubeInfo = await youtubeInfoPromise;
      const compositeId = await saveVideoToFirestore(trimmedUrl, youtubeInfo);
      
      console.log('✅ 影片已保存，compositeId:', compositeId);
      
      // 🔧 修復：導航時傳遞完整的 compositeId
      navigateToMainPage(trimmedUrl, youtubeInfo, compositeId);
      setYoutubeDialogOpen(false);
      setIsProcessing(false);

      // 🚀 在後台執行查詢操作（不影響導航）
      Promise.allSettled([
        // 查詢現有影片
        searchExistingVideo(videoId).then(searchResult => {
          // 调试日志已移除
          return searchResult;
        }).catch(error => {
          console.warn('⚠️ 後台查詢失敗:', error);
          return { hasMyVideo: false, hasOtherVideo: false };
        })
      ]).then(() => {
        console.log('🎉 後台查詢完成');
      });

    } catch (error) {
      console.error('❌ 處理 YouTube URL 失敗:', error);
      setUrlError('處理影片時發生錯誤，請重試');
      setIsProcessing(false);
    }
  };

  // 處理搜尋結果對話框的按鈕點擊
  const handleCreateNotes = async () => {
    if (videoSearchResult) {
      // 🚀 優化：立即導航到 MainPage
      // 🔧 修復：不需要再次調用 saveVideoToFirestore，因為 handleYouTubeConfirm 已經保存過了
      // 调试日志已移除
      
      // 立即導航到 MainPage
      navigateToMainPage(videoSearchResult.videoUrl, videoSearchResult.youtubeInfo);
      setSearchResultDialogOpen(false);
    }
  };

  const handleViewExpertNotes = () => {
    if (videoSearchResult) {
      navigateToMainPage(videoSearchResult.videoUrl, videoSearchResult.youtubeInfo);
    }
    setSearchResultDialogOpen(false);
  };

  // 處理本機檔案按鈕點擊
  const handleLocalFileClick = () => {
    console.log('📁 選擇本機檔案');
    // 觸發檔案選擇對話框
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // 處理檔案選擇
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      console.log('📁 選擇檔案:', file.name);
      
      // 驗證檔案類型
      const validTypes = ['video/mp4', 'video/x-m4v', 'video/mpeg', 'video/quicktime'];
      if (!validTypes.includes(file.type.toLowerCase())) {
        setUrlError('請選擇 MP4、M4V、MPEG 或 MOV 格式的影片');
        return;
      }
      
      // 顯示檔案選擇對話框
      setSelectedFile(file);
      setFileSelectionDialogOpen(true);
    }
  };

  // 🔧 新增：檔案選擇對話框處理函數
  const handleLocalPlay = async () => {
    if (selectedFile) {
      console.log('🎬 選擇本地播放:', selectedFile.name);
      
      // 🔧 修復：直接導航到 MainPage 播放，不需要複製檔案
      // MainPage 會自動創建 Blob URL 進行播放
      // 调试日志已移除
      
      // 🔧 修復：設置用戶主動播放標記，確保本地檔案也能記錄觀看記錄
      (window as any).__checkplay_user_initiated_play = true;
      console.log('✅ OpenPage: 已標記為用戶主動播放（本地檔案）');
      
      // 導航到 MainPage 並傳遞檔案信息
      navigate('/main', { 
        state: { 
          localFile: selectedFile,
          source: 'local',
          fromOpenPage: true // 標記來自 OpenPage
        } 
      });
      
      // 關閉對話框
      setFileSelectionDialogOpen(false);
      
      console.log('✅ 已導航到 MainPage，準備播放本機檔案');
    }
  };

  const handleUploadToCloud = async () => {
    if (selectedFile) {
      console.log('☁️ 選擇上傳雲端:', selectedFile.name);
      setIsUploading(true);
      setFileSelectionDialogOpen(false);
      
      try {
        // 導航到上傳頁面或處理上傳邏輯
        navigate('/upload', {
          state: {
            selectedFile: selectedFile,
            source: 'openpage'
          }
        });
      } catch (error) {
        console.error('上傳失敗:', error);
        setUrlError('上傳失敗，請重試');
        setIsUploading(false);
      }
    }
  };

  // 處理瀏覽記錄按鈕點擊
  const handleHistoryClick = () => {
    const isLoggedIn = !!authUser?.email;
    console.log('📚 前往瀏覽記錄，用戶登入狀態:', isLoggedIn);
    
    if (isEmbedded && onPageChange) {
      // 🔧 方案 A：訪客進入 communityPublic（已統一社群分頁）
      const targetSection = isLoggedIn ? 'personalHistory' : 'communityPublic';
      const targetExpandedMain = isLoggedIn 
        ? ['home', 'personalCenter', 'personalHistory'] 
        : ['home', 'community', 'communityPublic'];
      
      // 调试日志已移除
      onPageChange('manage', {
        currentSection: targetSection,
        expandedMain: targetExpandedMain
      });
    } else {
      // 傳統路由模式下，使用 navigate
      // 调试日志已移除
      navigate('/manage', { 
        state: { 
          currentSection: isLoggedIn ? 'personalHistory' : 'communityPublic'
        } 
      });
    }
  };

  // 處理閱讀筆記按鈕點擊
  const handleNotesClick = () => {
    const isLoggedIn = !!authUser?.email;
    console.log('📖 前往閱讀筆記，用戶登入狀態:', isLoggedIn);
    
    // 🔧 修復：支持未登入用戶直接跳轉到筆記頁面查看社群筆記
    if (isEmbedded && onPageChange) {
      // SPA 模式下，使用回調函數切換頁面
      // 调试日志已移除
      onPageChange('notes');
    } else {
      // 傳統路由模式下，使用 navigate
      // 调试日志已移除
      navigate('/notes');
    }
  };

  // 處理登入按鈕點擊
  const handleSignInClick = () => {
    console.log('🔐 開啟登入對話框');
    console.log('🔐 當前用戶狀態:', !!authUser?.email);
    console.log('🔐 設置 authDialogOpen 為 true');
    setAuthDialogOpen(true);
  };

  // 🔧 新增：強制刷新功能
  const handleForceRefresh = () => {
    // 调试日志已移除
    checkAndClearInfiniteLoops();
    setTimeout(() => {
      forceRefresh();
    }, 100);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: { xs: 2, sm: 4, md: 6 },
        position: 'relative'
      }}
    >
      {/* 主要內容區域 - 白色容器在畫面中置中，縮小10%寬度，20%高度 */}
      <Box
        sx={{
          textAlign: 'center',
          maxWidth: '540px', // 600px * 0.9 = 540px
          width: '90%', // 縮小10%
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderRadius: '24px',
          padding: { xs: 1.92, sm: 2.56, md: 3.84 }, // 再縮小20%：2.4*0.8=1.92, 3.2*0.8=2.56, 4.8*0.8=3.84
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}
      >
        {/* CheckPlay Logo 和品牌名稱 */}
        <Box sx={{ mb: 3.84 }}> {/* 4.8 * 0.8 = 3.84 */}
          <Box
            sx={{
              width: '120px',
              height: '120px',
              margin: '0 auto 24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 32px rgba(0, 191, 255, 0.3)'
            }}
          >
            <img
              src="/CheckPlay_Logo-0416_Logo-Only_300px.png"
              alt="CheckPlay Logo"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain'
              }}
            />
          </Box>
          
          <Typography
            variant="h2"
            component="h1"
            sx={{
              fontWeight: 700,
              background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem' },
              letterSpacing: '-0.02em'
            }}
          >
            CheckPlay
          </Typography>
          
          <Typography
            variant="h6"
            sx={{
              color: 'text.secondary',
              mt: 2,
              fontWeight: 400,
              opacity: 0.8
            }}
          >
            智能影片播放與管理平台
          </Typography>
        </Box>

        {/* 選擇影片來源 */}
        <Box sx={{ mb: 3.84 }}> {/* 4.8 * 0.8 = 3.84 */}
          <Typography
            variant="h5"
            sx={{
              mb: 2.56, // 3.2 * 0.8 = 2.56
              fontWeight: 600,
              color: 'text.primary'
            }}
          >
            選擇影片來源
          </Typography>
          
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 1.92, // 2.4 * 0.8 = 1.92
              justifyContent: 'center',
              alignItems: 'center',
              maxWidth: '600px',
              mx: 'auto'
            }}
          >
            {/* YouTube 按鈕 */}
            <Button
              variant="contained"
              size="large"
              onClick={handleYouTubeClick}
              startIcon={<YouTubeIcon />}
              sx={{
                backgroundColor: '#ff0000',
                color: 'white',
                px: 4,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: '16px',
                minWidth: '200px',
                height: '60px',
                '&:hover': {
                  backgroundColor: '#d32f2f',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(255, 0, 0, 0.3)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              YouTube
            </Button>

            {/* 本機檔案按鈕 */}
            <Button
              variant="outlined"
              size="large"
              onClick={handleLocalFileClick}
              startIcon={<FolderOpenIcon />}
              sx={{
                borderColor: '#1976d2',
                color: '#1976d2',
                px: 4,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: '16px',
                minWidth: '200px',
                height: '60px',
                borderWidth: '2px',
                '&:hover': {
                  borderColor: '#1565c0',
                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(25, 118, 210, 0.2)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              本機檔案
            </Button>

            {/* 閱讀筆記按鈕 */}
            <Button
              variant="outlined"
              size="large"
              onClick={handleNotesClick}
              startIcon={<BookmarkIcon />}
              sx={{
                borderColor: '#ff9800',
                color: '#ff9800',
                px: 4,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: '16px',
                minWidth: '200px',
                height: '60px',
                borderWidth: '2px',
                '&:hover': {
                  borderColor: '#f57c00',
                  backgroundColor: 'rgba(255, 152, 0, 0.04)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(255, 152, 0, 0.2)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              閱讀筆記
            </Button>

            {/* 瀏覽記錄按鈕 */}
            <Button
              variant="outlined"
              size="large"
              onClick={handleHistoryClick}
              startIcon={<HistoryIcon />}
              sx={{
                borderColor: '#4caf50',
                color: '#4caf50',
                px: 4,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: '16px',
                minWidth: '200px',
                height: '60px',
                borderWidth: '2px',
                '&:hover': {
                  borderColor: '#388e3c',
                  backgroundColor: 'rgba(76, 175, 80, 0.04)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(76, 175, 80, 0.2)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              瀏覽記錄
            </Button>

            {/* 🔧 新增：強制刷新按鈕 */}
            <Button
              variant="outlined"
              size="small"
              onClick={handleForceRefresh}
              sx={{
                borderColor: '#ff9800',
                color: '#ff9800',
                px: 2,
                py: 1,
                fontSize: '0.8rem',
                fontWeight: 500,
                borderRadius: '8px',
                minWidth: '120px',
                height: '36px',
                borderWidth: '1px',
                '&:hover': {
                  borderColor: '#f57c00',
                  backgroundColor: 'rgba(255, 152, 0, 0.04)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(255, 152, 0, 0.2)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              🧹 清除緩存
            </Button>

          </Box>
        </Box>

        {/* 用戶狀態顯示 - 根據登入狀態顯示不同內容 */}
        <Box sx={{ mt: 2.4, textAlign: 'center', transform: 'translateY(-20px)' }}>
          {authUser ? (
            // 已登入用戶顯示
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, height: '60px' }}>
              <Typography
                variant="h6"
                sx={{
                  color: '#1976d2',
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  transition: 'all 0.3s ease'
                }}
              >
                Hi～{authUser.name || authUser.email?.split('@')[0] || '用戶'}
              </Typography>
              <Button
                variant="text"
                onClick={() => {
                  // 使用 Firebase Auth 的 signOut 方法
                  import('firebase/auth').then(({ signOut, getAuth }) => {
                    const auth = getAuth();
                    signOut(auth).then(() => {
                      console.log('用戶已登出');
                      navigate('/');
                    }).catch((error) => {
                      console.error('登出失敗:', error);
                    });
                  });
                }}
                sx={{
                  color: '#1976d2',
                  fontSize: '0.75rem',
                  px: 1,
                  py: 0,
                  minWidth: 'auto',
                  textDecoration: 'underline',
                  '&:hover': {
                    backgroundColor: 'transparent',
                    textDecoration: 'none'
                  }
                }}
              >
                登出
              </Button>
            </Box>
          ) : (
            // 未登入用戶顯示
            <Tooltip title="登入 CheckPlay" placement="top">
              <Typography
                variant="h6"
                onClick={handleSignInClick}
                sx={{
                  color: '#1976d2',
                  cursor: 'pointer',
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  height: '60px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&:hover': {
                    color: '#1565c0',
                    textDecoration: 'underline'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Sign in / Login
              </Typography>
            </Tooltip>
          )}
        </Box>

        {/* 版本資訊顯示 */}
        <Box sx={{ mt: 1, textAlign: 'center', transform: 'translateY(-20px)' }}>
          <Typography
            variant="caption"
            sx={{
              color: '#666',
              fontSize: '0.7rem',
              fontWeight: 400,
              opacity: 0.8,
              display: 'block',
              letterSpacing: '0.5px'
            }}
          >
            CheckPlay {import.meta.env.VITE_APP_VERSION || '0.1112.05'}
          </Typography>
        </Box>

        {/* 隱藏的檔案輸入 */}
        <input
          ref={fileInputRef}
          type="file"
          accept="video/*"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
      </Box>

      {/* 版權宣告 */}
      <Box
        sx={{
          position: 'absolute',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          textAlign: 'center'
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: 'rgba(255, 255, 255, 0.8)',
            fontSize: '0.8rem',
            opacity: 0.7
          }}
        >
          © 2025 CheckPlay. All rights reserved.
        </Typography>
      </Box>

      {/* YouTube URL 輸入對話框 */}
      <Dialog
        open={youtubeDialogOpen}
        onClose={() => setYoutubeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          🎬 輸入 YouTube 影片網址
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          <TextField
            autoFocus
            margin="dense"
            label="YouTube 網址"
            type="url"
            fullWidth
            variant="outlined"
            placeholder="https://www.youtube.com/watch?v=..."
            value={youtubeUrl}
            onChange={(e) => {
              setYoutubeUrl(e.target.value);
              setUrlError(null); // 清除錯誤訊息
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !isProcessing) {
                handleYouTubeConfirm();
              }
            }}
            error={!!urlError}
            helperText={urlError || '支援 YouTube 影片、Shorts、直播等格式'}
            disabled={isProcessing}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px'
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center' }}>
          <Button
            onClick={() => {
              setYoutubeDialogOpen(false);
              setUrlError(null);
              setYoutubeUrl('');
            }}
            variant="outlined"
            disabled={isProcessing}
            sx={{ borderRadius: '8px', px: 3 }}
          >
            取消
          </Button>
          <Button
            onClick={handleYouTubeConfirm}
            variant="contained"
            disabled={!youtubeUrl.trim() || isProcessing}
            startIcon={isProcessing ? undefined : <PlayArrowIcon />}
            sx={{ 
              borderRadius: '8px', 
              px: 3,
              backgroundColor: '#1976d2',
              '&:hover': {
                backgroundColor: '#1565c0'
              }
            }}
          >
            {isProcessing ? '處理中...' : '開始播放'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 搜尋結果對話框 */}
      <Dialog 
        open={searchResultDialogOpen} 
        onClose={() => setSearchResultDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          {videoSearchResult?.hasOtherVideo ? '影片已有人收藏' : '您是首位收藏家'}
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', py: 2 }}>
          {videoSearchResult?.hasOtherVideo ? (
            <Box>
              <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                影片已有 {videoSearchResult.uniqueUserCount} 人收藏
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                共有 {videoSearchResult.totalNotes} 個重點
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                您可以觀看專家的筆記，或建立自己的筆記
              </Typography>
            </Box>
          ) : (
            <Box>
              <Typography variant="h6" sx={{ mb: 2, color: 'success.main' }}>
                🎉 您是首位收藏家
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                這支影片還沒有其他人收藏過
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                按「建立筆記」開始為這支影片建立重點
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center', gap: 2 }}>
          {videoSearchResult?.hasOtherVideo && (
            <Button
              onClick={handleViewExpertNotes}
              variant="outlined"
              startIcon={<VisibilityIcon />}
              sx={{ borderRadius: '8px', px: 3 }}
            >
              觀看專家筆記
            </Button>
          )}
          <Button
            onClick={handleCreateNotes}
            variant="contained"
            startIcon={<EditIcon />}
            sx={{ 
              borderRadius: '8px', 
              px: 3,
              backgroundColor: videoSearchResult?.hasOtherVideo ? '#1976d2' : '#4caf50',
              '&:hover': {
                backgroundColor: videoSearchResult?.hasOtherVideo ? '#1565c0' : '#45a049'
              }
            }}
          >
            建立筆記
          </Button>
        </DialogActions>
      </Dialog>

      {/* 🔧 新增：檔案選擇對話框 */}
      <Dialog
        open={fileSelectionDialogOpen}
        onClose={() => setFileSelectionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleLocalPlay(); // Enter 鍵快速選擇預設選項
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          📁 檔案選擇
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Typography variant="body1" gutterBottom>
              已選擇檔案：<strong>{selectedFile?.name}</strong>
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => handleLocalPlay()}
              autoFocus
              sx={{ 
                py: 2,
                fontSize: '1.1rem',
                backgroundColor: '#1976d2',
                color: 'white',
                height: 'auto',
                minHeight: '80px',
                '&:hover': {
                  backgroundColor: '#1565c0'
                },
                '&:focus': {
                  backgroundColor: '#1976d2',
                  boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.2)'
                }
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  🎬 僅用 Checkplay 瀏覽
                </Box>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)', lineHeight: 1.2 }}>
                  本地播放，不上傳雲端（預設選項）
                </Typography>
              </Box>
            </Button>
            
            <Button
              variant="outlined"
              size="large"
              onClick={() => handleUploadToCloud()}
              sx={{ 
                py: 2,
                fontSize: '1.1rem',
                borderColor: '#2e7d32',
                color: '#2e7d32',
                height: 'auto',
                minHeight: '80px',
                '&:hover': {
                  borderColor: '#1b5e20',
                  backgroundColor: '#f1f8e9'
                }
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  ☁️ 將檔案上傳雲端
                </Box>
                <Typography variant="caption" sx={{ lineHeight: 1.2 }}>
                  上傳到雲端並進入檔案總管
                </Typography>
              </Box>
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* 🔧 新增：上傳進度對話框 */}
      <Dialog
        open={isUploading}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          ☁️ 上傳中...
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Typography variant="body1" gutterBottom>
              正在上傳檔案：<strong>{selectedFile?.name}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              請稍候，正在處理中...
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      {/* 登入對話框 */}
      <AuthDialog
        open={authDialogOpen}
        onClose={() => setAuthDialogOpen(false)}
        onShowRegister={() => {
          // 可以在這裡處理註冊邏輯，或者保持登入模式
          console.log('顯示註冊表單');
        }}
      />

      {/* 🆕 頁尾：隱私權政策和服務條款連結 */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          py: 2,
          textAlign: 'center',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          borderTop: '1px solid',
          borderColor: 'divider',
          zIndex: 1000
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
          © 2025 Red Ball Studio | CheckPlay 簡播
        </Typography>
        <Box sx={{ mt: 0.5, display: 'flex', justifyContent: 'center', gap: 2 }}>
          <Typography
            component="a"
            href="https://checkplay.me/privacy.html"
            target="_blank"
            rel="noopener noreferrer"
            variant="caption"
            sx={{
              color: 'primary.main',
              textDecoration: 'none',
              fontSize: '0.75rem',
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
          >
            隱私權政策
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            |
          </Typography>
          <Typography
            component="a"
            href="https://checkplay.me/terms.html"
            target="_blank"
            rel="noopener noreferrer"
            variant="caption"
            sx={{
              color: 'primary.main',
              textDecoration: 'none',
              fontSize: '0.75rem',
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
          >
            服務條款
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default OpenPage;
